/**
 * @fileoverview Inoreader webhook schemas for webhook payload validation

 */

import { z } from '@hono/zod-openapi';

export const InoreaderWebhookItemSchema = z.object({
  crawlTimeMsec: z.string().openapi({
    description: 'Crawl time in milliseconds as string',
    example: '1642248000000'
  }),
  timestampUsec: z.string().openapi({
    description: 'Timestamp in microseconds as string',
    example: '1642248000000000'
  }),
  id: z.string().openapi({
    description: 'Unique Inoreader item identifier',
    example: 'tag:google.com,2005:reader/item/000000001234abcd'
  }),
  categories: z.array(z.string()).openapi({
    description: 'Array of category/tag strings from Inoreader',
    example: ['user/1005921515/state/com.google/reading-list']
  }),
  title: z.string().openapi({
    description: 'Title of the article/item',
    example: 'Understanding Serverless Architecture'
  }),
  published: z.number().openapi({
    description: 'Published timestamp as Unix epoch',
    example: 1642248000
  }),
  updated: z.number().openapi({
    description: 'Updated timestamp as Unix epoch',
    example: 1642248000
  }),
  canonical: z.array(
    z.object({
      href: z.string().url().openapi({
        description: 'Canonical URL of the item',
        example: 'https://example.com/article'
      }),
    }).openapi({
      description: 'Canonical link object'
    })
  ).openapi({
    description: 'Array of canonical URLs',
    example: [{ href: 'https://example.com/article' }]
  }),
  alternate: z.array(
    z.object({
      href: z.string().url().openapi({
        description: 'Alternate URL',
        example: 'https://example.com/article'
      }),
      type: z.string().openapi({
        description: 'MIME type of the alternate link',
        example: 'text/html'
      }),
    }).openapi({
      description: 'Alternate link object'
    })
  ).openapi({
    description: 'Array of alternate URLs with types',
    example: [{ href: 'https://example.com/article', type: 'text/html' }]
  }),
  summary: z.object({
    direction: z.string().openapi({
      description: 'Text direction (ltr/rtl)',
      example: 'ltr'
    }),
    content: z.string().openapi({
      description: 'HTML content summary/excerpt',
      example: '<p>This article covers serverless architecture...</p>'
    }),
  }).openapi({
    description: 'Summary content with direction'
  }),
  author: z.string().openapi({
    description: 'Author name',
    example: 'John Doe'
  }),
  likingUsers: z.array(z.unknown()).openapi({
    description: 'Array of users who liked this item'
  }),
  comments: z.array(z.unknown()).openapi({
    description: 'Array of comments on this item'
  }),
  commentsNum: z.number().openapi({
    description: 'Number of comments',
    example: 5
  }),
  annotations: z.array(z.unknown()).openapi({
    description: 'Array of annotations/notes'
  }),
  origin: z.object({
    streamId: z.string().openapi({
      description: 'Feed stream identifier',
      example: 'feed/https://example.com/rss'
    }),
    title: z.string().openapi({
      description: 'Source feed title',
      example: 'Tech Blog'
    }),
    htmlUrl: z.string().url().openapi({
      description: 'Source website URL',
      example: 'https://example.com'
    }),
  }).openapi({
    description: 'Information about the source feed'
  }),
}).openapi({
  description: 'Individual item from Inoreader webhook',
  example: {
    crawlTimeMsec: '1642248000000',
    timestampUsec: '1642248000000000',
    id: 'tag:google.com,2005:reader/item/000000001234abcd',
    categories: ['user/1005921515/state/com.google/reading-list'],
    title: 'Understanding Serverless Architecture',
    published: 1642248000,
    updated: 1642248000,
    canonical: [{ href: 'https://example.com/article' }],
    alternate: [{ href: 'https://example.com/article', type: 'text/html' }],
    summary: { direction: 'ltr', content: '<p>This article covers...</p>' },
    author: 'John Doe',
    likingUsers: [],
    comments: [],
    commentsNum: 0,
    annotations: [],
    origin: {
      streamId: 'feed/https://example.com/rss',
      title: 'Tech Blog',
      htmlUrl: 'https://example.com'
    }
  }
});

export const InoreaderWebhookPayloadSchema = z.object({
  items: z.array(InoreaderWebhookItemSchema).openapi({
    description: 'Array of webhook items from Inoreader',
    example: [
      {
        crawlTimeMsec: '1642248000000',
        timestampUsec: '1642248000000000',
        id: 'tag:google.com,2005:reader/item/000000001234abcd',
        title: 'Understanding Serverless Architecture'
      }
    ]
  }),
}).openapi({
  description: 'Webhook payload containing array of items from Inoreader',
  example: {
    items: [
      {
        crawlTimeMsec: '1642248000000',
        timestampUsec: '1642248000000000',
        id: 'tag:google.com,2005:reader/item/000000001234abcd',
        categories: ['user/1005921515/state/com.google/reading-list'],
        title: 'Understanding Serverless Architecture',
        published: 1642248000,
        updated: 1642248000,
        canonical: [{ href: 'https://example.com/article' }],
        alternate: [{ href: 'https://example.com/article', type: 'text/html' }],
        summary: { direction: 'ltr', content: '<p>This article covers...</p>' },
        author: 'John Doe',
        likingUsers: [],
        comments: [],
        commentsNum: 0,
        annotations: [],
        origin: {
          streamId: 'feed/https://example.com/rss',
          title: 'Tech Blog',
          htmlUrl: 'https://example.com'
        }
      }
    ]
  }
});
